import { useEffect, useState } from "react";
import axios from "axios";
import {
  API_DISTRICTS,
  API_SUB_COUNTIES,
  API_COUNTIES,
  API_PARISHES,
  API_VILLAGES,
} from "../../utils/apis";
import { BASE_API_URL } from "../../config";
import { useAuth } from "../../context/AuthContext";
import { TFormHookParams } from "../../types/common";

export type TLocationField =
  | "district"
  | "county"
  | "sub_county"
  | "parish"
  | "village"
  | "physical_address";

export const ALL_LOCATION_FIELDS: TLocationField[] = [
  "district",
  "county",
  "sub_county",
  "parish",
  "village",
  "physical_address",
];

// Cascade order. Each included field is filtered by the nearest
// included field above it (e.g. without "county", sub_county is
// filtered by district).
const CHAIN: TLocationField[] = [
  "district",
  "county",
  "sub_county",
  "parish",
  "village",
];

type TLocationParams = TFormHookParams & {
  include?: TLocationField[];
  labels?: Partial<Record<TLocationField, string>>;
  types?: Partial<Record<TLocationField, string>>;
};

export const useLocationDataForm = ({
  form,
  include = ALL_LOCATION_FIELDS,
  labels = {},
  types = {},
}: TLocationParams = {}) => {
  const [districts, setDistricts] = useState([]);
  const [counties, setCounties] = useState([]);
  const [sub_counties, setSubCounties] = useState([]);
  const [parishes, setParishes] = useState([]);
  const [villages, setVillages] = useState([]);

  const [districtsLoading, setDistrictsLoading] = useState(false);
  const [countiesLoading, setCountiesLoading] = useState(false);
  const [subCountiesLoading, setSubCountiesLoading] = useState(false);
  const [parishesLoading, setParishesLoading] = useState(false);
  const [villagesLoading, setVillagesLoading] = useState(false);

  const { authToken } = useAuth();

  const has = (f: TLocationField) => include.includes(f);

  // value of the nearest included ancestor in the cascade
  const parentValue = (f: TLocationField) => {
    const chain = CHAIN.filter(has);
    const i = chain.indexOf(f);
    return i > 0 ? (form as any)?.[chain[i - 1]] : undefined;
  };

  const dataCall = (api: any, setData: any, params: any, setLoading: any) => {
    setLoading(true);
    axios
      .get(`${BASE_API_URL}${api}`, {
        headers: {
          "Content-Type": "application/json",
          ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
        },
        params: { ...params },
      })
      .then(({ data }) => {
        setData(Array.isArray(data) ? data : data?.data || data?.results || []);
      })
      .catch(() => {
        setData([]);
      })
      .finally(() => setLoading(false));
  };

  const loadChild = (api: any, setData: any, parent: any, setLoading: any) => {
    if (!parent) {
      setData([]); // parent cleared -> clear stale options
      return;
    }
    dataCall(api, setData, { parent }, setLoading);
  };

  const hasDistrict = has("district");
  const hasCounty = has("county");
  const hasSubCounty = has("sub_county");
  const hasParish = has("parish");
  const hasVillage = has("village");

  const countyParent = parentValue("county");
  const subCountyParent = parentValue("sub_county");
  const parishParent = parentValue("parish");
  const villageParent = parentValue("village");

  useEffect(() => {
    if (hasDistrict) {
      dataCall(API_DISTRICTS, setDistricts, null, setDistrictsLoading);
    }
  }, [hasDistrict]);

  useEffect(() => {
    if (hasCounty)
      loadChild(API_COUNTIES, setCounties, countyParent, setCountiesLoading);
  }, [hasCounty, countyParent]);

  useEffect(() => {
    if (hasSubCounty)
      loadChild(
        API_SUB_COUNTIES,
        setSubCounties,
        subCountyParent,
        setSubCountiesLoading,
      );
  }, [hasSubCounty, subCountyParent]);

  useEffect(() => {
    if (hasParish)
      loadChild(API_PARISHES, setParishes, parishParent, setParishesLoading);
  }, [hasParish, parishParent]);

  useEffect(() => {
    if (hasVillage)
      loadChild(API_VILLAGES, setVillages, villageParent, setVillagesLoading);
  }, [hasVillage, villageParent]);

  // `optional` lists fields that stay non-mandatory even when `required` is true
  const fields = (required = false, optional: TLocationField[] = []) => {
    const isRequired = (f: TLocationField) => required && !optional.includes(f);

    const select = (
      field_name: TLocationField,
      defaultLabel: string,
      options_data: any[],
      loading: boolean,
    ) => {
      const label = labels[field_name] ?? defaultLabel;
      return {
        field_name,
        type: "select",
        label,
        options_data,
        loading,
        props: {
          required: isRequired(field_name),
          smallWidth: true,
          placeholder: `Select ${label}`,
        },
      };
    };

    const all = [
      select("district", "District", districts, districtsLoading),
      select("county", "County", counties, countiesLoading),
      select("sub_county", "Sub County", sub_counties, subCountiesLoading),
      select("parish", "Parish", parishes, parishesLoading),
      select("village", "Village", villages, villagesLoading),
      {
        field_name: "physical_address" as TLocationField,
        type: types.physical_address ?? "text",
        label: labels.physical_address ?? "Physical Address",
        description:
          "Enter the full and accurate physical location where you or the business can be found. Include details such as building name, street, village, parish, or district.",
        props: {
          required: isRequired("physical_address"),
          placeholder: "Enter Physical Address",
        },
      },
    ];

    return all.filter((f) => has(f.field_name));
  };

  return {
    fields,
    options: {
      districts,
      counties,
      sub_counties,
      parishes,
      villages,
    },
  };
};
